/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Loading } from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../store';
import { areFeaturesUpdating } from '../../store/features/selectors';
import { getInstance } from '../../store/instance/actions';
import { selectIsNewIa } from '../../store/instance/selectors';
import { selectAccessDenied } from '../../store/messages/selectors';
import { updateTenant, getAccount } from '../../store/session/actions';
import { getAccountIdByEnvironment } from '../../util/account';
import * as crnUtil from '../../util/crn';
import NotFound from '../errors/NotFound';
import Unauthorized from '../errors/Unauthorized';
import { InstanceManagement as messages } from './messages';

/**
 * This component issues the action that loads the instance information
 * and shows a loading page until the instance data has been received.
 *
 * For the time being, a property tells it whether it is being loaded from the
 * New IA build or the Classic IA build, and it uses that information to
 * determine whether the opposite experience is being visualized. If that is
 * the case, it forces a full-page reload, which will cause the server to
 * serve the statically-built code for the correct experience, thus
 * essentially changing from one experience to the other.
 *
 * The future goal is to use the instance's value stating whether it is New
 * IA or Classic IA to lazily load the New IA or Classic IA routes, WITHOUT
 * triggering a full-page reload (although the Redux state would have to
 * be cleared to avoid issues).
 */

interface InstanceLoaderProps {
  InstanceRoutesClassicIa: React.ComponentType;
  InstanceRoutesNewIa: React.ComponentType;
}

const InstanceLoader: React.FC<InstanceLoaderProps> = ({
  InstanceRoutesClassicIa,
  InstanceRoutesNewIa
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const { instance } = useParams<{ instance?: string }>();
  const [isNotFound, setIsNotFound] = useState(false);
  const accessDenied = useAppSelector(selectAccessDenied);
  const { search } = useLocation();

  const isLoading = useAppSelector(
    state =>
      areFeaturesUpdating(state) ||
      !state.session.instance ||
      !state.session.account
  );

  const isNewIa = useAppSelector(selectIsNewIa);

  useEffect(() => {
    if (crnUtil.isCrn(instance)) {
      const decodedInstance = decodeURIComponent(instance);
      const { region } = crnUtil.parse(decodedInstance);
      const accountId = getAccountIdByEnvironment(decodedInstance);
      dispatch(updateTenant(region, decodedInstance));
      dispatch(getAccount(accountId));
      dispatch(getInstance({ search }));
    } else {
      setIsNotFound(true);
    }
  }, [dispatch, instance, search]);

  if (instance && isNotFound) {
    return <NotFound />;
  }

  if (isLoading) {
    return <Loading data-testid="loading" />;
  }

  if (accessDenied) {
    return <Unauthorized />;
  }

  // Decide which instance routes to render depending on whether the instance
  // is New IA or Classic IA.
  const InstanceRoutes = isNewIa
    ? InstanceRoutesNewIa
    : InstanceRoutesClassicIa;

  return (
    <div
      role="main"
      aria-label={intl.formatMessage(messages.instanceManagement)}>
      <InstanceRoutes />
    </div>
  );
};

export default InstanceLoader;
